import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Dumbbell, Heart, Camera, Clock, CheckCircle, ArrowRight, Loader2, CreditCard, Shield } from "lucide-react";
import { format } from "date-fns";

// Convert 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Success Page Component
export const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");
  const sessionId = searchParams.get("session_id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("checking");

  useEffect(() => {
    const fetchBookingAndStatus = async () => {
      // Poll for checkout status if we have session_id
      if (sessionId) {
        try {
          const statusResponse = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
          setPaymentStatus(statusResponse.data.payment_status);
        } catch (e) {
          console.error("Error checking payment status", e);
          setPaymentStatus("error");
        }
      }
      
      // Fetch booking details
      if (bookingId) {
        try {
          const response = await axios.get(`${API}/bookings/${bookingId}`);
          setBooking(response.data);
        } catch (e) {
          console.error("Error fetching booking", e);
        }
      }
      setLoading(false);
    };
    fetchBookingAndStatus();
  }, [bookingId, sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E10600]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-20" data-testid="booking-success">
      <div className="max-w-lg mx-auto px-4">
        <Card className="bg-[#111111] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-display text-3xl text-white mb-4">BOOKING CONFIRMED!</h1>
            <p className="text-gray-400 mb-6">Your deposit has been received and your session is booked.</p>
            
            {booking && (
              <div className="bg-[#1a1a1a] rounded-lg p-4 text-left mb-6">
                <p className="text-gray-400 text-sm mb-2">Booking Details:</p>
                <p className="text-white"><strong>Service:</strong> {booking.service_name}</p>
                <p className="text-white"><strong>Date:</strong> {booking.date}</p>
                <p className="text-white"><strong>Time:</strong> {formatTime12Hour(booking.time)}</p>
                <p className="text-[#E10600] font-bold mt-2">Deposit Paid: ${booking.deposit_amount?.toFixed(2)}</p>
                <p className="text-gray-400">Balance Due: ${(booking.price - (booking.deposit_amount || 0)).toFixed(2)}</p>
              </div>
            )}
            
            <p className="text-gray-500 text-sm mb-6">A confirmation email has been sent to your email address.</p>
            
            <a href="/" className="cta-btn-primary inline-block px-6 py-3 rounded">
              Return Home
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Cancel Page Component
export const BookingCancel = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] py-20" data-testid="booking-cancel">
      <div className="max-w-lg mx-auto px-4">
        <Card className="bg-[#111111] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="font-display text-3xl text-white mb-4">PAYMENT CANCELLED</h1>
            <p className="text-gray-400 mb-6">Your booking is not confirmed. No payment was processed.</p>
            <p className="text-gray-500 text-sm mb-6">You can try booking again or contact us if you need assistance.</p>
            
            <div className="flex gap-4 justify-center">
              <a href="/booking" className="cta-btn-primary inline-block px-6 py-3 rounded">
                Try Again
              </a>
              <a href="/contact" className="bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white inline-block px-6 py-3 rounded">
                Contact Us
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Booking Form Component
const BookingForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialCategory = searchParams.get("category") || "training";
  
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: ""
  });

  const categoryIcons = {
    training: <Dumbbell className="w-5 h-5" />,
    massage: <Heart className="w-5 h-5" />,
    media: <Camera className="w-5 h-5" />
  };

  const categoryLabels = {
    training: "Performance Training",
    massage: "Sports Massage",
    media: "Media Services"
  };

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services?category=${activeCategory}`);
        setServices(response.data);
      } catch (error) {
        toast.error("Failed to load services");
      }
    };
    fetchServices();
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots([]);
  }, [activeCategory]);

  // Fetch available slots when date is selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate || !activeCategory) return;
      
      setLoading(true);
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/slots?date=${dateStr}&category=${activeCategory}`);
        setAvailableSlots(response.data);
      } catch (error) {
        toast.error("Failed to load available times");
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
    setSelectedTime(null);
  }, [selectedDate, activeCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate || !selectedTime) {
      toast.error("Please complete all booking details");
      return;
    }

    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Please fill in all contact information");
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        service_id: selectedService.service_id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        notes: formData.notes
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;
      
      toast.success("Booking created! Redirecting to payment...");
      
      // Create checkout session
      await handlePayment(booking.booking_id);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
      setSubmitting(false);
    }
  };

  const handlePayment = async (bookingId) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      toast.error("Payment processing failed. Please try again.");
      setProcessingPayment(false);
      setSubmitting(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const depositAmount = selectedService ? (selectedService.price * 0.20).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="booking-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-[#111111] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl md:text-6xl text-white mb-4">
            BOOK YOUR <span className="text-[#E10600]">SESSION</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Select your service, pick a time, and get ready to perform.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Category Tabs - Only Training and Massage */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="w-full grid grid-cols-3 bg-[#111111] border border-[#2A2A2A] mb-8" data-testid="category-tabs">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <TabsTrigger 
                key={key} 
                value={key}
                className="data-[state=active]:bg-[#E10600] data-[state=active]:text-white flex items-center gap-2 py-3"
                data-testid={`tab-${key}`}
              >
                {categoryIcons[key]}
                <span className="hidden sm:inline">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Step 1: Select Service */}
            <div>
              <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#E10600] flex items-center justify-center text-sm">1</span>
                SELECT SERVICE
              </h2>
              <div className="space-y-3" data-testid="services-list">
                {services.map((service) => (
                  <Card 
                    key={service.service_id}
                    className={`cursor-pointer transition-all ${
                      selectedService?.service_id === service.service_id 
                        ? "bg-[#E10600]/10 border-[#E10600]" 
                        : "bg-[#111111] border-[#2A2A2A] hover:border-[#E10600]/50"
                    }`}
                    onClick={() => setSelectedService(service)}
                    data-testid={`service-option-${service.service_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-white font-medium">{service.name}</h3>
                          <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                            <Clock className="w-4 h-4" />
                            {service.duration} min
                          </div>
                        </div>
                        <span className="text-[#E10600] font-bold">${service.price}</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-2">{service.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Step 2: Select Date & Time */}
            <div>
              <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#E10600] flex items-center justify-center text-sm">2</span>
                SELECT DATE & TIME
              </h2>
              
              <Card className="bg-[#111111] border-[#2A2A2A] mb-4">
                <CardContent className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={isDateDisabled}
                    className="rounded-md"
                    data-testid="booking-calendar"
                  />
                </CardContent>
              </Card>

              {selectedDate && (
                <Card className="bg-[#111111] border-[#2A2A2A]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg">
                      Available Times - {format(selectedDate, "MMM d, yyyy")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#E10600]" />
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.slot_id}
                            onClick={() => setSelectedTime(slot.time)}
                            className={`time-slot ${selectedTime === slot.time ? "selected" : ""}`}
                            data-testid={`time-slot-${slot.time}`}
                          >
                            {formatTime12Hour(slot.time)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-4">No available times for this date</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Step 3: Contact Info */}
            <div>
              <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#E10600] flex items-center justify-center text-sm">3</span>
                YOUR DETAILS
              </h2>
              
              <Card className="bg-[#111111] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        className="form-input mt-1"
                        placeholder="Your name"
                        data-testid="input-name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                        className="form-input mt-1"
                        placeholder="your@email.com"
                        data-testid="input-email"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-300">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        className="form-input mt-1"
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-gray-300">Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="form-input mt-1"
                        placeholder="Any specific requests or information..."
                        rows={3}
                        data-testid="input-notes"
                      />
                    </div>

                    {/* Booking Summary with Payment Info */}
                    {selectedService && selectedDate && selectedTime && (
                      <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#2A2A2A]" data-testid="booking-summary">
                        <h4 className="font-bold text-white mb-3">Booking Summary</h4>
                        <div className="space-y-1 text-sm text-gray-300">
                          <p><span className="text-gray-500">Service:</span> {selectedService.name}</p>
                          <p><span className="text-gray-500">Date:</span> {format(selectedDate, "MMMM d, yyyy")}</p>
                          <p><span className="text-gray-500">Time:</span> {formatTime12Hour(selectedTime)}</p>
                          <p><span className="text-gray-500">Duration:</span> {selectedService.duration} min</p>
                        </div>
                        <div className="border-t border-[#2A2A2A] mt-3 pt-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Session Price:</span>
                            <span className="text-white">${selectedService.price}</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-gray-400">Deposit (20%):</span>
                            <span className="text-[#E10600] font-bold">${depositAmount}</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-gray-400">Balance Due at Session:</span>
                            <span className="text-gray-300">${(selectedService.price - parseFloat(depositAmount)).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full cta-btn-primary"
                      disabled={!selectedService || !selectedDate || !selectedTime || submitting || processingPayment}
                      data-testid="submit-booking"
                    >
                      {submitting || processingPayment ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          PAY DEPOSIT ${depositAmount}
                        </>
                      )}
                    </Button>
                    
                    <p className="text-gray-500 text-xs text-center flex items-center justify-center gap-1">
                      <Shield className="w-3 h-3" />
                      Secure payment powered by Stripe
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

// Main BookingPage wrapper that handles routing
const BookingPage = () => {
  const location = useLocation();
  
  if (location.pathname === "/booking/success") {
    return <BookingSuccess />;
  }
  if (location.pathname === "/booking/cancel") {
    return <BookingCancel />;
  }
  
  return <BookingForm />;
};

export default BookingPage;
